import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../hooks/useAuth';
import { useDispatch, useSelector } from 'react-redux';
import { removeItem, updateQuantity } from '../store/slices/cartSlice';

export default function Cart() {
  const { user, isAuthenticated, login } = useAuth();
  const dispatch = useDispatch();
  const cartState = useSelector((state) => state.cart) || { items: [], total: 0 };

  // Debug logging
  console.log('Auth state:', { isAuthenticated, user });
  console.log('Cart state:', cartState);

  const handleUpdateQuantity = (itemId, newQuantity) => {
    try {
      const quantity = Math.max(1, parseInt(newQuantity) || 1);
      console.log('Updating quantity:', { itemId, quantity });
      dispatch(updateQuantity({ id: itemId, quantity }));
    } catch (error) {
      console.error('Error updating quantity:', error);
      alert('Error updating quantity. Please try again.');
    }
  };

  const handleRemoveItem = (itemId) => {
    try {
      console.log('Removing item:', itemId);
      dispatch(removeItem(itemId));
    } catch (error) {
      console.error('Error removing item:', error);
      alert('Error removing item. Please try again.');
    }
  };

  if (!isAuthenticated) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Please login to view your cart
          </Typography>
          <Button variant="contained" color="primary" onClick={() => login()} sx={{ mt: 2 }}>
            Login
          </Button>
        </Box>
      </Container>
    );
  }

  // Check if cart state is valid
  if (!cartState || !Array.isArray(cartState.items)) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">
            There was an error loading your cart. Please try refreshing the page.
          </Alert>
        </Box>
      </Container>
    );
  }

  const { items: cartItems, total } = cartState;

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Your Cart ({cartItems.length} items)
        </Typography>
        
        {cartItems.length === 0 ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography>Your cart is empty</Typography>
            <Button
              component={RouterLink}
              to="/products"
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
            >
              Continue Shopping
            </Button>
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              {cartItems.map((item) => (
                <Grid item xs={12} key={item.id}>
                  <Card>
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                          <Typography variant="h6">{item.name}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography color="text.secondary">
                            ${item.price}
                          </Typography>
                        </Grid>
                        <Grid item xs={8} sm={3}>
                          <TextField
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleUpdateQuantity(item.id, e.target.value)}
                            InputProps={{ 
                              inputProps: { min: 1 },
                              sx: { width: '80px' }
                            }}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={4} sm={2}>
                          <IconButton
                            onClick={() => handleRemoveItem(item.id)}
                            color="error"
                            size="large"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Subtotal: ${(item.price * item.quantity).toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 4, textAlign: 'right' }}>
              <Typography variant="h6" gutterBottom>
                Total: ${total.toFixed(2)}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
              >
                Proceed to Checkout
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Container>
  );
}
